import { DEFAULT_SCORING_CONFIG } from "./config.js";
import { PoseLandmarkIndex } from "./landmarks.js";
function midpoint(a, b) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}
function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
}
function mean(values) {
    return values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
}
/**
 * Interior angle at `vertex`, between the segments to `a` and `b`.
 * Returns a value in [0, PI]; 0 means fully folded, PI means straight.
 */
export function angleAt(a, vertex, b) {
    const v1x = a.x - vertex.x;
    const v1y = a.y - vertex.y;
    const v2x = b.x - vertex.x;
    const v2y = b.y - vertex.y;
    const magnitude = Math.hypot(v1x, v1y) * Math.hypot(v2x, v2y);
    if (magnitude < 1e-9) {
        // Coincident points carry no angular information.
        return 0;
    }
    const cosine = (v1x * v2x + v1y * v2y) / magnitude;
    // Guard against floating-point drift outside acos's domain.
    return Math.acos(Math.min(1, Math.max(-1, cosine)));
}
/** Direction of the segment a->b, in radians, wrapped to [-PI, PI]. */
export function segmentAngle(a, b) {
    return Math.atan2(b.y - a.y, b.x - a.x);
}
/** Smallest signed difference between two angles, in [-PI, PI]. */
export function angleDifference(a, b) {
    let delta = (a - b) % (2 * Math.PI);
    if (delta > Math.PI) {
        delta -= 2 * Math.PI;
    }
    else if (delta < -Math.PI) {
        delta += 2 * Math.PI;
    }
    return delta;
}
function landmarkPoint(landmarks, index) {
    const landmark = landmarks[index];
    return landmark && landmark.visibility > 0 ? { x: landmark.x, y: landmark.y } : undefined;
}
function visibilityOf(landmarks, indices) {
    if (indices.length === 0) {
        return 0;
    }
    // The weakest landmark governs: an elbow angle is only as trustworthy as the
    // least visible of the three points that define it.
    let lowest = 1;
    for (const index of indices) {
        lowest = Math.min(lowest, landmarks[index]?.visibility ?? 0);
    }
    return lowest;
}
/**
 * Body scale used to normalise relative positions.
 *
 * Torso length and shoulder width are combined because either alone degenerates
 * for some poses: bending forward shortens the apparent torso, and turning
 * sideways collapses shoulder width.
 */
export function computeBodyScale(landmarks) {
    const leftShoulder = landmarkPoint(landmarks, PoseLandmarkIndex.leftShoulder);
    const rightShoulder = landmarkPoint(landmarks, PoseLandmarkIndex.rightShoulder);
    const leftHip = landmarkPoint(landmarks, PoseLandmarkIndex.leftHip);
    const rightHip = landmarkPoint(landmarks, PoseLandmarkIndex.rightHip);
    if (leftShoulder && rightShoulder && leftHip && rightHip) {
        const shoulderCentre = midpoint(leftShoulder, rightShoulder);
        const hipCentre = midpoint(leftHip, rightHip);
        const torsoLength = distance(shoulderCentre, hipCentre);
        const shoulderWidth = distance(leftShoulder, rightShoulder);
        const torsoScale = torsoLength * 0.7 + shoulderWidth * 0.3;
        if (torsoScale > 1e-6) {
            return torsoScale;
        }
    }
    // A cropped body may still expose a useful torso width or a single limb.
    // Use a visible local scale instead of requiring shoulders and hips together.
    const shoulderWidth = leftShoulder && rightShoulder ? distance(leftShoulder, rightShoulder) : 0;
    const hipWidth = leftHip && rightHip ? distance(leftHip, rightHip) : 0;
    const segmentLengths = [];
    for (const [aIndex, bIndex] of [
        [PoseLandmarkIndex.leftShoulder, PoseLandmarkIndex.leftElbow],
        [PoseLandmarkIndex.rightShoulder, PoseLandmarkIndex.rightElbow],
        [PoseLandmarkIndex.leftElbow, PoseLandmarkIndex.leftWrist],
        [PoseLandmarkIndex.rightElbow, PoseLandmarkIndex.rightWrist],
        [PoseLandmarkIndex.leftHip, PoseLandmarkIndex.leftKnee],
        [PoseLandmarkIndex.rightHip, PoseLandmarkIndex.rightKnee],
        [PoseLandmarkIndex.leftKnee, PoseLandmarkIndex.leftAnkle],
        [PoseLandmarkIndex.rightKnee, PoseLandmarkIndex.rightAnkle],
    ]) {
        const a = landmarks[aIndex];
        const b = landmarks[bIndex];
        if (a && b && a.visibility > 0 && b.visibility > 0) {
            const length = distance(a, b);
            if (length > 1e-6) {
                segmentLengths.push(length);
            }
        }
    }
    const localScale = segmentLengths.length > 0 ? mean(segmentLengths) : 0;
    return Math.max(shoulderWidth, hipWidth, localScale);
}
/** Hip midpoint, the origin all relative positions are measured from. */
export function computeHipCentre(landmarks) {
    const leftHip = landmarkPoint(landmarks, PoseLandmarkIndex.leftHip);
    const rightHip = landmarkPoint(landmarks, PoseLandmarkIndex.rightHip);
    return leftHip && rightHip ? midpoint(leftHip, rightHip) : undefined;
}
function computePoseOrigin(landmarks) {
    const hipCentre = computeHipCentre(landmarks);
    if (hipCentre) {
        return hipCentre;
    }
    const leftShoulder = landmarkPoint(landmarks, PoseLandmarkIndex.leftShoulder);
    const rightShoulder = landmarkPoint(landmarks, PoseLandmarkIndex.rightShoulder);
    if (leftShoulder && rightShoulder) {
        return midpoint(leftShoulder, rightShoulder);
    }
    const visible = landmarks.filter((landmark) => landmark.visibility > 0);
    if (visible.length === 0) {
        return undefined;
    }
    return {
        x: mean(visible.map((landmark) => landmark.x)),
        y: mean(visible.map((landmark) => landmark.y)),
    };
}
/**
 * Extracts scoring features from a single pose.
 *
 * Returns undefined when no visible landmarks or usable local scale exist.
 * Partial poses use a visible torso or limb as their normalization anchor;
 * confidence controls which body-part groups can score.
 */
export function extractPoseFeatures(landmarks) {
    if (landmarks.length === 0) {
        return undefined;
    }
    const hipCentre = computePoseOrigin(landmarks);
    const bodyScale = computeBodyScale(landmarks);
    if (!hipCentre || bodyScale < 1e-6) {
        // Without a torso there is no meaningful scale, and relative positions
        // would explode. Better to report nothing than to report noise.
        return undefined;
    }
    const point = (index) => landmarkPoint(landmarks, index) ?? { x: 0, y: 0 };
    const L = PoseLandmarkIndex;
    const leftShoulder = point(L.leftShoulder);
    const rightShoulder = point(L.rightShoulder);
    const leftHip = point(L.leftHip);
    const rightHip = point(L.rightHip);
    const shoulderCentre = midpoint(leftShoulder, rightShoulder);
    const hipPair = computeHipCentre(landmarks) ?? hipCentre;
    const shoulderPair = landmarkPoint(landmarks, L.leftShoulder) && landmarkPoint(landmarks, L.rightShoulder)
        ? shoulderCentre
        : hipCentre;
    const relativeTo = (index, origin) => {
        const p = point(index);
        return { x: (p.x - origin.x) / bodyScale, y: (p.y - origin.y) / bodyScale };
    };
    return {
        angles: {
            leftElbow: angleAt(leftShoulder, point(L.leftElbow), point(L.leftWrist)),
            rightElbow: angleAt(rightShoulder, point(L.rightElbow), point(L.rightWrist)),
            leftShoulder: angleAt(point(L.leftElbow), leftShoulder, leftHip),
            rightShoulder: angleAt(point(L.rightElbow), rightShoulder, rightHip),
            leftHip: angleAt(leftShoulder, leftHip, point(L.leftKnee)),
            rightHip: angleAt(rightShoulder, rightHip, point(L.rightKnee)),
            leftKnee: angleAt(leftHip, point(L.leftKnee), point(L.leftAnkle)),
            rightKnee: angleAt(rightHip, point(L.rightKnee), point(L.rightAnkle)),
        },
        positions: {
            leftWrist: relativeTo(L.leftWrist, shoulderPair),
            rightWrist: relativeTo(L.rightWrist, shoulderPair),
            leftAnkle: relativeTo(L.leftAnkle, hipPair),
            rightAnkle: relativeTo(L.rightAnkle, hipPair),
        },
        orientation: {
            shoulderLine: segmentAngle(leftShoulder, rightShoulder),
            hipLine: segmentAngle(leftHip, rightHip),
            torsoLean: segmentAngle(hipCentre, shoulderCentre),
        },
        confidence: {
            leftArm: visibilityOf(landmarks, [L.leftShoulder, L.leftElbow, L.leftWrist]),
            rightArm: visibilityOf(landmarks, [L.rightShoulder, L.rightElbow, L.rightWrist]),
            leftLeg: visibilityOf(landmarks, [L.leftHip, L.leftKnee, L.leftAnkle]),
            rightLeg: visibilityOf(landmarks, [L.rightHip, L.rightKnee, L.rightAnkle]),
            torso: visibilityOf(landmarks, [L.leftShoulder, L.rightShoulder, L.leftHip, L.rightHip]),
        },
        bodyScale,
    };
}
/**
 * Velocities of the extremities, in body-scale units per second.
 *
 * Computed over a fixed look-back rather than between adjacent frames: the
 * player streams at ~15 Hz and the reference is sampled at 10 Hz, so raw
 * frame-to-frame deltas describe different time spans and cannot be compared.
 */
export function extractMotionFeatures(history, atMs, config = DEFAULT_SCORING_CONFIG) {
    const current = interpolateLandmarksAt(history, atMs);
    const past = interpolateLandmarksAt(history, atMs - config.motionLookbackMs);
    if (!current || !past) {
        return undefined;
    }
    const currentFeatures = extractPoseFeatures(current);
    const pastFeatures = extractPoseFeatures(past);
    if (!currentFeatures || !pastFeatures) {
        return undefined;
    }
    const seconds = config.motionLookbackMs / 1000;
    const velocity = (a, b) => ({
        x: (a.x - b.x) / seconds,
        y: (a.y - b.y) / seconds,
    });
    return {
        leftWrist: velocity(currentFeatures.positions.leftWrist, pastFeatures.positions.leftWrist),
        rightWrist: velocity(currentFeatures.positions.rightWrist, pastFeatures.positions.rightWrist),
        leftAnkle: velocity(currentFeatures.positions.leftAnkle, pastFeatures.positions.leftAnkle),
        rightAnkle: velocity(currentFeatures.positions.rightAnkle, pastFeatures.positions.rightAnkle),
        confidence: {
            leftWrist: Math.min(current[PoseLandmarkIndex.leftWrist]?.visibility ?? 0, past[PoseLandmarkIndex.leftWrist]?.visibility ?? 0),
            rightWrist: Math.min(current[PoseLandmarkIndex.rightWrist]?.visibility ?? 0, past[PoseLandmarkIndex.rightWrist]?.visibility ?? 0),
            leftAnkle: Math.min(current[PoseLandmarkIndex.leftAnkle]?.visibility ?? 0, past[PoseLandmarkIndex.leftAnkle]?.visibility ?? 0),
            rightAnkle: Math.min(current[PoseLandmarkIndex.rightAnkle]?.visibility ?? 0, past[PoseLandmarkIndex.rightAnkle]?.visibility ?? 0),
        },
    };
}
/**
 * Landmarks at an arbitrary time, linearly interpolated between samples.
 *
 * Returns undefined outside the covered range, or when either neighbouring
 * sample has no detected pose: interpolating across a gap would invent a
 * dancer who was never there.
 */
export function interpolateLandmarksAt(history, atMs) {
    if (history.length === 0) {
        return undefined;
    }
    const first = history[0];
    const last = history[history.length - 1];
    if (!first || !last || atMs < first.tMs || atMs > last.tMs) {
        return undefined;
    }
    // Binary search for the sample at or before atMs.
    let low = 0;
    let high = history.length - 1;
    let index = 0;
    while (low <= high) {
        const mid = (low + high) >> 1;
        const sample = history[mid];
        if (!sample) {
            break;
        }
        if (sample.tMs <= atMs) {
            index = mid;
            low = mid + 1;
        }
        else {
            high = mid - 1;
        }
    }
    const before = history[index];
    if (!before) {
        return undefined;
    }
    if (before.tMs === atMs || index === history.length - 1) {
        return before.landmarks.length > 0 ? before.landmarks : undefined;
    }
    const after = history[index + 1];
    if (!after) {
        return undefined;
    }
    if (before.landmarks.length === 0 || after.landmarks.length === 0) {
        return undefined;
    }
    if (before.landmarks.length !== after.landmarks.length) {
        return before.landmarks;
    }
    const span = after.tMs - before.tMs;
    const t = span <= 0 ? 0 : (atMs - before.tMs) / span;
    return before.landmarks.map((landmark, i) => {
        const next = after.landmarks[i];
        if (!next) {
            return landmark;
        }
        return {
            x: landmark.x + t * (next.x - landmark.x),
            y: landmark.y + t * (next.y - landmark.y),
            z: landmark.z + t * (next.z - landmark.z),
            visibility: Math.min(landmark.visibility, next.visibility),
        };
    });
}
//# sourceMappingURL=features.js.map